if (!customElements.get('card-media-carousel')) {
  customElements.define(
    'card-media-carousel',
    class CardMediaCarousel extends HTMLElement {
      constructor() {
        super();

        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        this.slides = Array.from(this.track.children);
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        // lives as a sibling of .card__media (outside this element) so its
        // z-index can escape .card__media's stacking context — see
        // component-card.css for why.
        const cardInner = this.closest('.card__inner');
        this.progressThumb = cardInner ? cardInner.querySelector('[data-carousel-progress-thumb]') : null;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.hydrated = false;
        this.suppressNextTrackClick = false;
        this.arrowsInitialized = false;

        this.onPrevClick = this.onPrevClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onFirstInteraction = this.onFirstInteraction.bind(this);
        this.onTrackClick = this.onTrackClick.bind(this);

        if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('click', this.onTrackClick);

        ['pointerenter', 'touchstart', 'focusin'].forEach((eventName) => {
          this.addEventListener(eventName, this.onFirstInteraction, { passive: true });
        });

        // Desktop hover preview of the 2nd image (collection grid only, via
        // data-hover-preview; master list #39). Cross-fades an overlay copy of
        // slide 2 over slide 1 instead of scrolling, so it feels like a classic
        // hover swap; the arrows still work (next goes on to image 3).
        this.hoverPreview = this.hasAttribute('data-hover-preview') && this.slides.length > 1;
        if (this.hoverPreview) {
          this.onHoverEnter = this.onHoverEnter.bind(this);
          this.onHoverLeave = this.onHoverLeave.bind(this);
          this.addEventListener('pointerenter', this.onHoverEnter);
          this.addEventListener('pointerleave', this.onHoverLeave);
        }

        this.hydrateSlide(0);
        this.updateArrows(0);
        this.updateProgress(0);
        this.observeProximity();
      }

      observeProximity() {
        if (!('IntersectionObserver' in window)) {
          this.hydrateSlide(1);
          return;
        }
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.hydrateSlide(1);
                observer.disconnect();
              }
            });
          },
          { rootMargin: '200px' }
        );
        observer.observe(this);
      }

      onHoverEnter(event) {
        if (event.pointerType !== 'mouse') return;
        if (!window.matchMedia('(min-width: 990px) and (hover: hover)').matches) return;
        if (this.currentIndex() !== 0) return;
        this.hydrateSlide(1);
        if (!this.hoverImg) {
          const source = this.slides[1].querySelector('img');
          if (!source) return;
          this.hoverImg = source.cloneNode(false);
          this.hoverImg.removeAttribute('loading');
          this.hoverImg.className = 'card__media-carousel-hover-img';
          this.hoverImg.alt = '';
          this.hoverImg.setAttribute('aria-hidden', 'true');
          this.slides[0].appendChild(this.hoverImg);
        }
        this.classList.add('is-hover-preview');
        this.updateProgress(1);
      }

      onHoverLeave() {
        if (!this.classList.contains('is-hover-preview')) return;
        this.classList.remove('is-hover-preview');
        this.updateProgress(this.currentIndex());
      }

      onFirstInteraction() {
        if (this.hydrated) return;
        this.hydrated = true;
        this.slides.forEach((_, index) => this.hydrateSlide(index));
      }

      hydrateSlide(index) {
        const slide = this.slides[index];
        if (!slide) return;
        const img = slide.querySelector('img[data-carousel-src]');
        if (!img) return;
        if (img.dataset.carouselSrcset) img.srcset = img.dataset.carouselSrcset;
        img.src = img.dataset.carouselSrc;
        img.removeAttribute('data-carousel-src');
        img.removeAttribute('data-carousel-srcset');
      }

      currentIndex() {
        const width = this.track.clientWidth || 1;
        return Math.round(this.track.scrollLeft / width);
      }

      goTo(index) {
        const width = this.track.clientWidth;
        const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
        this.track.scrollTo({
          left: clamped * width,
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        });
      }

      onPrevClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.onFirstInteraction();
        this.goTo(this.currentIndex() - 1);
      }

      onNextClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.onFirstInteraction();
        // Image 2 is already showing via the hover preview: carry on to image 3
        // (or, with only 2 images, settle on slide 2 without a visible jump).
        if (this.classList.contains('is-hover-preview')) {
          this.classList.remove('is-hover-preview');
          if (this.slides.length > 2) {
            this.goTo(2);
          } else {
            this.track.scrollTo({ left: this.track.clientWidth, behavior: 'auto' });
          }
          return;
        }
        this.goTo(this.currentIndex() + 1);
      }

      onTrackClick() {
        if (this.suppressNextTrackClick) {
          this.suppressNextTrackClick = false;
          return;
        }
        const url = this.dataset.productUrl;
        if (!url) return;
        window.location.href = url;
      }

      onScroll() {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          const index = this.currentIndex();
          this.hydrateSlide(index);
          this.hydrateSlide(index + 1);
          this.updateArrows(index);
          this.updateProgress(index);
        });
      }

      updateArrows(index) {
        const prevWasVisible = this.arrowsInitialized && this.prevButton && !this.prevButton.hidden;
        const nextWasVisible = this.arrowsInitialized && this.nextButton && !this.nextButton.hidden;
        const prevHidden = index <= 0;
        const nextHidden = index >= this.slides.length - 1;
        if (this.prevButton) this.prevButton.hidden = prevHidden;
        if (this.nextButton) this.nextButton.hidden = nextHidden;
        // An arrow that just disappeared (reaching the first/last slide) leaves
        // nothing to intercept the very next click in that spot, so it falls
        // through to onTrackClick and navigates to the product unexpectedly.
        // Swallow exactly that one stray click.
        if ((prevWasVisible && prevHidden) || (nextWasVisible && nextHidden)) {
          this.suppressNextTrackClick = true;
        }
        this.arrowsInitialized = true;
      }

      updateProgress(index) {
        if (!this.progressThumb) return;
        this.progressThumb.style.setProperty('--carousel-progress-index', index);
      }
    }
  );
}
